/**
 * Phase 9.6 — 📜 Payout Policy
 * ----------------------------
 * Public read-only policy page covering minimum payout, hold period,
 * non-cashout currencies, fraud, and chargebacks. Linked from /payouts
 * and the wallet.
 */
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";

const Bullet = ({ children }) => (
  <li className="flex gap-2 text-sm text-pink-200/85 leading-relaxed">
    <span className="text-pink-400 shrink-0">•</span>
    <span>{children}</span>
  </li>
);

const PayoutPolicyPage = () => (
  <div className="min-h-screen bg-gradient-to-b from-[#0a0514] via-[#0a0a14] to-black text-white">
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="payout-policy-page">
      <div className="mb-6">
        <Link to="/payouts" className="text-xs text-pink-300 hover:text-pink-200 underline">← Back to Payouts</Link>
      </div>
      <h1 className="text-3xl sm:text-4xl font-black mb-2">📜 Payout Policy</h1>
      <p className="text-pink-200/70 text-sm mb-8">
        Last updated · Feb 2026. Plain-language summary; the binding version lives in the Creator Terms.
      </p>

      <Card className="p-5 mb-4 bg-gradient-to-br from-pink-500/10 to-purple-500/10 border-pink-500/30">
        <h2 className="text-lg font-bold mb-2">Minimum payout</h2>
        <ul className="space-y-2">
          <Bullet>The minimum cash-out amount is <strong className="text-white">$25.00 USD</strong>.</Bullet>
          <Bullet>Requests below the minimum are blocked until your available balance crosses the threshold.</Bullet>
        </ul>
      </Card>

      <Card className="p-5 mb-4 bg-black/30 border-pink-500/20">
        <h2 className="text-lg font-bold mb-2">Review & pending hold</h2>
        <ul className="space-y-2">
          <Bullet>Every paid-coin gift creates a <strong className="text-white">7-day pending hold</strong> before its
            earnings become withdrawable. This protects creators from chargeback clawbacks.</Bullet>
          <Bullet>Payouts are <strong className="text-white">manually reviewed</strong>. Expect 1–3 business days
            from request → approval → payment.</Bullet>
          <Bullet>RGM may delay or deny a payout if the request triggers a fraud / abuse review.</Bullet>
        </ul>
      </Card>

      <Card className="p-5 mb-4 bg-black/30 border-pink-500/20">
        <h2 className="text-lg font-bold mb-2">What's withdrawable — and what isn't</h2>
        <ul className="space-y-2">
          <Bullet>💎 <strong className="text-white">Paid Coins</strong> (bought through Stripe) → gifts using them
            create withdrawable creator earnings (after the platform cut).</Bullet>
          <Bullet>🪙 <strong className="text-white">Reward Coins</strong> (quests, streaks, spotlight prizes,
            sponsored promo coins, event tokens) → these are <strong className="text-rose-300">not cashable</strong>.
            They can be spent on gifts, votes, and event entries, but never convert to real money.</Bullet>
          <Bullet>🧪 <strong className="text-white">Test Coins</strong> (admin/sandbox grants) → never
            withdrawable, never count toward creator earnings, never affect public rankings.</Bullet>
        </ul>
      </Card>

      <Card className="p-5 mb-4 bg-black/30 border-pink-500/20">
        <h2 className="text-lg font-bold mb-2">Chargebacks, refunds, and fraud</h2>
        <ul className="space-y-2">
          <Bullet>If a purchase that funded your earnings is reversed (chargeback / Stripe refund), the
            corresponding portion of your available balance is <strong className="text-white">reduced</strong>.</Bullet>
          <Bullet>Coin laundering, self-gifting, multi-account abuse, or any fraudulent activity may
            <strong className="text-white"> void</strong> the related earnings entirely.</Bullet>
          <Bullet>You're responsible for the accuracy of your payout details. Funds sent to a wrong handle
            may not be recoverable.</Bullet>
        </ul>
      </Card>

      <Card className="p-5 mb-4 bg-black/30 border-pink-500/20">
        <h2 className="text-lg font-bold mb-2">Payment methods (early phase)</h2>
        <ul className="space-y-2">
          <Bullet>PayPal · Cash App · Zelle · Manual / Other (case-by-case).</Bullet>
          <Bullet>Coming soon: Stripe Connect Express (automated payouts), tax forms, agency splits.</Bullet>
        </ul>
      </Card>

      <Card className="p-5 mb-4 bg-emerald-500/10 border-emerald-400/40" data-testid="policy-rate-sheet">
        <h2 className="text-lg font-bold mb-3">💰 Rate sheet (the full picture)</h2>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-emerald-200/70">
            <tr>
              <th className="text-left pb-1">Pack</th>
              <th className="text-right pb-1">Price</th>
              <th className="text-right pb-1">Coins received</th>
              <th className="text-right pb-1">Creator gets (75%)</th>
              <th className="text-right pb-1">Cash-out (× $0.0075)</th>
            </tr>
          </thead>
          <tbody className="text-emerald-100">
            <tr><td>Starter</td><td className="text-right">$1.99</td><td className="text-right">100</td><td className="text-right">75</td><td className="text-right font-mono">$0.56</td></tr>
            <tr><td>Fan Boost</td><td className="text-right">$4.99</td><td className="text-right">550</td><td className="text-right">412</td><td className="text-right font-mono">$3.09</td></tr>
            <tr><td>Super Supporter</td><td className="text-right">$9.99</td><td className="text-right">1,200</td><td className="text-right">900</td><td className="text-right font-mono">$6.75</td></tr>
            <tr><td>GOAT Supporter</td><td className="text-right">$49.99</td><td className="text-right">7,000</td><td className="text-right">5,250</td><td className="text-right font-mono">$39.38</td></tr>
          </tbody>
        </table>
        <p className="text-xs text-emerald-100/80 mt-3">
          Three numbers fully determine the economy: <strong>75%</strong> creator share,{" "}
          <strong>$0.0075</strong> USD per earned coin, <strong>$25</strong> minimum cash-out.
        </p>
      </Card>

      <Card className="p-5 mb-4 bg-black/30 border-pink-500/20" data-testid="policy-worked-example">
        <h2 className="text-lg font-bold mb-2">A worked example</h2>
        <ol className="space-y-2 text-sm text-pink-200/85 list-decimal list-inside leading-relaxed">
          <li>
            A fan buys the <strong className="text-white">Super Supporter</strong> pack for $9.99 →
            their wallet gains <strong className="text-white">1,200 coins</strong> (1,000 paid + 200 bonus).
          </li>
          <li>
            They send all 1,200 coins to your live room as gifts.
          </li>
          <li>
            RGM takes the 25% platform cut → you earn{" "}
            <strong className="text-white">900 earned coins</strong>.
          </li>
          <li>
            The 7-day hold ticks down. After it expires those 900 coins move from
            "pending" to "available."
          </li>
          <li>
            At any point your available balance ≥ <strong>$25</strong> (= 3,334 coins) you can request a payout.
            900 coins × $0.0075 = <strong className="text-white">$6.75</strong> in your favour.
          </li>
          <li>
            You submit the payout, RGM reviews it (1–3 business days), and pays via your chosen method.
          </li>
        </ol>
      </Card>

      <Card className="p-5 mb-4 bg-black/30 border-pink-500/20" data-testid="policy-funding">
        <h2 className="text-lg font-bold mb-2">How payouts are funded</h2>
        <ul className="space-y-2">
          <Bullet>
            Every dollar paid to a creator is sourced from <strong className="text-white">real Stripe revenue</strong> on a
            successful coin purchase. RGM never advances money on coins that haven't been bought.
          </Bullet>
          <Bullet>
            Reward / promo / test coins are explicitly non-cashable (see above). Only "paid" coins ever
            count toward your withdrawable balance.
          </Bullet>
          <Bullet>
            Our admin payout audit verifies this lineage end-to-end before any disbursement. Auditor-friendly
            CSVs are available on request.
          </Bullet>
        </ul>
      </Card>

      <Card className="p-5 mb-4 bg-amber-500/10 border-amber-400/40">
        <h2 className="text-lg font-bold mb-2">Conversion rate</h2>
        <ul className="space-y-2">
          <Bullet>Earned coins convert at <strong className="text-amber-100">$0.0075 per earned coin</strong>{" "}
            (0.75¢). 1,000 earned coins ≈ $7.50.</Bullet>
          <Bullet>This rate already reflects the platform cut. Stripe processing fees are not deducted on top.</Bullet>
        </ul>
      </Card>

      <p className="text-[11px] text-pink-200/50 mt-8">
        Questions? Email <a className="underline hover:text-pink-200" href="mailto:support@rgmstudio.org">support@rgmstudio.org</a>.
        RGM Studio reserves the right to update this policy with notice.
      </p>
    </div>
  </div>
);

export default PayoutPolicyPage;
